import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateLineQuantity, removeLine, format, setFreeSample } = useStore();
  const navigate = useNavigate();

  const items = cart?.line_items || [];
  const totals = cart?.totals || { subtotal: 0, tax: 0, shipping: 0, total: 0 };
  const ms = cart?.milestones || { progress_pct: 0, next_reward: "", unlocked: [] };

  const SAMPLES = [
    { id: "no-04-amber-veil", name: "Amber Veil" },
    { id: "no-11-citrus-cloud", name: "Citrus Cloud" },
    { id: "no-07-vanilla-mist", name: "Vanilla Mist" },
    { id: "no-13-santal-hush", name: "Santal Hush" },
    { id: "no-05-rose-hour", name: "Rose Hour" },
  ];

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent
        side="right"
        className="bg-[#FAF8F5] w-full sm:max-w-[440px] flex flex-col p-0 border-l border-black/10"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-black/10 sd-frosted">
          <SheetTitle className="font-serif text-2xl tracking-[0.02em]">Your Cart</SheetTitle>
        </SheetHeader>

        <div className="px-6 py-5 border-b border-black/10">
          <div className="text-xs text-[#1A1918]/80">{ms.next_reward}</div>
          <Progress value={ms.progress_pct} className="h-1.5 mt-2 bg-[#F1ECE4] [&_[data-slot=progress-indicator]]:bg-[#C5A059]" data-testid="cart-milestone-progress" />
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {["free_sample", "free_shipping", "luxury_kit"].map((k) => (
              <span key={k} className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                ms.unlocked?.includes(k) ? "bg-[#C5A059]/20 border-[#C5A059] text-[#1A1918]" : "border-black/10 text-[#1A1918]/50"
              }`}>
                {k.replace("_", " ")}
              </span>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div data-testid="cart-empty-state" className="h-full flex flex-col items-center justify-center text-center py-16">
              <div className="font-serif text-2xl text-[#1A1918]">Your cart is quiet.</div>
              <p className="text-sm text-[#1A1918]/60 mt-2 max-w-xs">Discover our extrait-strength scents inspired by iconic houses.</p>
              <Button
                data-testid="cart-empty-explore-button"
                className="mt-6 bg-[#C5A059] text-[#1A1918] hover:bg-[#C5A059]/90 rounded-xl"
                onClick={() => { setCartOpen(false); navigate("/shop"); }}
              >
                Explore Fragrances
              </Button>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {items.map((item, i) => (
                <motion.div
                  key={`${item.product_id}-${item.size_label}-${i}`}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: 40 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-4 py-4 border-b border-black/8"
                  data-testid={`cart-item-${i}`}
                >
                  <div className="w-20 h-24 rounded-lg overflow-hidden bg-[#F1ECE4]" style={{ backgroundImage: `url(${item.product.image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#1A1918]/60">{item.product.number}</div>
                    <Link to={`/product/${item.product.slug}`} onClick={() => setCartOpen(false)} className="font-serif text-lg hover:text-[#C5A059] transition-colors leading-tight block">
                      {item.product.name}
                    </Link>
                    <div className="text-xs text-[#1A1918]/60">{item.size_label} • Inspired by {item.product.inspired_by}</div>
                    {item.engraving && (
                      <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-[#C5A059]">Engraved: {item.engraving}</div>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1 border border-black/10 rounded-full px-1 py-0.5">
                        <button
                          data-testid={`cart-item-${i}-decrement`}
                          onClick={() => updateLineQuantity(i, item.quantity - 1)}
                          className="w-6 h-6 flex items-center justify-center hover:text-[#C5A059]"
                          aria-label="Decrease quantity"
                        ><Minus className="h-3 w-3" /></button>
                        <div data-testid={`cart-item-${i}-qty`} className="w-6 text-center text-xs">{item.quantity}</div>
                        <button
                          data-testid={`cart-item-${i}-increment`}
                          onClick={() => updateLineQuantity(i, item.quantity + 1)}
                          className="w-6 h-6 flex items-center justify-center hover:text-[#C5A059]"
                          aria-label="Increase quantity"
                        ><Plus className="h-3 w-3" /></button>
                      </div>
                      <div className="text-sm font-medium">{format(item.line_total)}</div>
                    </div>
                  </div>
                  <button
                    data-testid={`cart-item-${i}-remove`}
                    onClick={() => removeLine(i)}
                    className="text-[#1A1918]/40 hover:text-[#1A1918] self-start"
                    aria-label="Remove"
                  ><Trash2 className="h-4 w-4" /></button>
                </motion.div>
              ))}
            </AnimatePresence>
          )}

          {items.length > 0 && ms.unlocked?.includes("free_sample") && (
            <div className="mt-4 p-4 rounded-xl border border-[#C5A059]/40 bg-[#C5A059]/8">
              <div className="text-xs uppercase tracking-[0.18em] text-[#C5A059] mb-2">Choose Your Free 2ml Sample</div>
              <div className="flex flex-wrap gap-2">
                {SAMPLES.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`cart-free-sample-${s.id}`}
                    onClick={() => setFreeSample(s.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      cart?.free_sample === s.id ? "bg-[#C5A059]/20 border-[#C5A059] text-[#1A1918]" : "bg-[#FAF8F5] border-black/10 text-[#1A1918]/75 hover:border-[#C5A059]/40"
                    }`}
                  >{s.name}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-black/10 px-6 py-5 space-y-3 sd-frosted">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{format(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-[#1A1918]/70">
              <span>Shipping</span>
              <span className="text-[13px] text-[#1A1918]/60" data-testid="cart-shipping">Calculated at checkout</span>
            </div>
            <div className="flex justify-between text-sm text-[#1A1918]/70">
              <span>Estimated Tax</span>
              <span className="text-[13px] text-[#1A1918]/60" data-testid="cart-tax">Calculated at checkout</span>
            </div>
            <div className="flex justify-between text-base font-medium pt-2 border-t border-black/10">
              <span>Estimated Total</span>
              <span data-testid="cart-total">{format(totals.subtotal)}</span>
            </div>
            <Button
              data-testid="cart-checkout-button"
              onClick={() => { setCartOpen(false); navigate("/checkout"); }}
              className="w-full bg-[#C5A059] text-[#1A1918] hover:bg-[#C5A059]/90 rounded-xl py-6 text-sm tracking-[0.16em] uppercase shadow-[0_10px_30px_rgba(197,160,89,0.24)]"
            >
              Proceed to Checkout
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default CartDrawer;
